import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { KPI_RISK_INDEX } from './kpi_risk_index';
import { KPI_SUBCONTRACTOR } from './kpi_subcontractor';
import { KPI_WEEKLY_REVENUE } from './kpi_weekly_revenue';
import { KPI_COLLECTIONS } from './kpi_collections';
import { KPI_DEFECTS } from './kpi_defects';
import { KPI_TACHO } from './kpi_tacho';
import { KPI_ALCOHOL } from './kpi_alcohol';
import { KPI_SERVICE_COMPLIANCE } from './kpi_service_compliance';
import { KPI_STANDARD } from './kpi_standard';
import { KPI_WITH_TARGET } from './kpi_with_target';
import { KPI_RATIO } from './kpi_ratio';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

export const DashboardKPIs = ({
    processedDashboardData,
    activeKpis,
    features,
    theme,

    targetOperationalRisk,
    targetSubcontractedProfit,
    targetWeeklyRevenue,
    targetCollections,
    targetDefectResolution,
    targetTachoCompliance,
    targetDriverTesting,
    targetServiceCompliance,
    targetDieselUsage,
    targetOutstandingDefects,
    targetAdblueDieselRatioMin,
    targetAdblueDieselRatioMax,

    superRestricted,
    isCollectionsAllowed,
    isDefectsAllowed,
    isTachoAllowed,
    isAlcoholAllowed,
    isServiceAllowed,

    handleChangeTab,

    historicalKpis,
    kpiPeriod,
    kpiLoading,
    loadingPeriod,
    fetchHistoricalKpis,
    targetWeeklyRevenueAmount
}) => {

    return (

        <div style={{ padding: '20px' }}>
            {/* Top KPIs - Risk Score, Profit Margin, and Weekly Revenue */}
            <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '25px', 
                marginBottom: '30px'
            }}>
                {/* Operational Risk Index KPI */}
                {activeKpis?.operational_risk !== false && (
                    <KPI_RISK_INDEX
                        processedDashboardData={processedDashboardData}
                        targetOperationalRisk={targetOperationalRisk}
                        theme={theme}
                    />
                )}

                {/* Subcontracted Profit Margin KPI */}
                {activeKpis?.subcontracted_profit !== false && (
                    <KPI_SUBCONTRACTOR
                        processedDashboardData={processedDashboardData}
                        targetSubcontractedProfit={targetSubcontractedProfit}
                        theme={theme}
                    />
                )}

                {/* Weekly Revenue Trend KPI */}
                {activeKpis?.weekly_revenue !== false && (
                    <KPI_WEEKLY_REVENUE
                        processedDashboardData={processedDashboardData}
                        targetWeeklyRevenue={targetWeeklyRevenue}
                        theme={theme}
                    />
                )}

                {/* Collections KPI */}
                {activeKpis?.collections_kpi !== false && (
                    <KPI_COLLECTIONS
                        processedDashboardData={processedDashboardData}
                        targetCollectionsKpi={targetCollections}
                        isCollectionsAllowed={isCollectionsAllowed}
                        handleChangeTab={handleChangeTab}
                        theme={theme}
                    />
                )}
            </div>

            {/* Business KPI Cards */}
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
                    gap: '25px', 
                    marginBottom: '40px' 
                }}>
                {/* Defect Resolution KPI */}
                {activeKpis?.defect_resolution !== false && (
                    <KPI_DEFECTS
                        processedDashboardData={processedDashboardData}
                        targetDefectResolution={targetDefectResolution}
                        superRestricted={superRestricted}
                        isDefectsAllowed={isDefectsAllowed}
                        handleChangeTab={handleChangeTab}
                        theme={theme}
                    />
                )}
                
                {/* Tacho Infringements KPI */}
                {activeKpis?.tacho_compliance !== false && (
                    <KPI_TACHO
                        processedDashboardData={processedDashboardData}
                        targetTachoCompliance={targetTachoCompliance}
                        superRestricted={superRestricted}
                        isTachoAllowed={isTachoAllowed}
                        handleChangeTab={handleChangeTab}
                        theme={theme}
                    />
                )}
                
                {/* Driver Testing Compliance KPI */}
                {activeKpis?.driver_testing !== false && (
                    <KPI_ALCOHOL
                        processedDashboardData={processedDashboardData}
                        targetDriverTesting={targetDriverTesting}
                        superRestricted={superRestricted}
                        isAlcoholAllowed={isAlcoholAllowed}
                        handleChangeTab={handleChangeTab}
                        theme={theme}
                    />
                )}
                
                {/* Service Compliance KPI */}
                {activeKpis?.service_compliance !== false && (
                    <KPI_SERVICE_COMPLIANCE
                        processedDashboardData={processedDashboardData}
                        targetServiceCompliance={targetServiceCompliance}
                        isServiceAllowed={isServiceAllowed}
                        handleChangeTab={handleChangeTab}
                        theme={theme}
                    />
                )}

                {/* Diesel Usage KPI */}
                {activeKpis?.diesel_usage !== false && (
                    <KPI_STANDARD
                        processedDashboardData={processedDashboardData?.fuelKpis?.dieselThisMonth || 0}
                        title="DIESEL USAGE"
                        metric='litres'
                        icon="⛽"
                        range="This Month"
                        color="#28a745"
                        theme={theme}
                    />
                )}

                {/* AdBlue Usage KPI */}
                {activeKpis?.adblue_usage !== false && (
                    <KPI_STANDARD
                        processedDashboardData={processedDashboardData?.fuelKpis?.adblueThisMonth || 0}
                        title="ADBLUE USAGE"
                        metric='litres'
                        icon="💧"
                        range="This Month"
                        color="#05abec"
                        theme={theme}
                    />
                )}

                {/* Litres per KM KPI */}
                {activeKpis?.average_mpg !== false && (
                    <KPI_WITH_TARGET
                        processedDashboardData={processedDashboardData?.fuelKpis?.litresPerKm || 0}
                        targetValue={targetDieselUsage}
                        title="LITRES PER KM"
                        metric='L/km'
                        icon="📊"
                        range="This Month"
                        theme={theme}
                    />
                )}

                {/* AdBlue/Diesel Ratio KPI */}
                {activeKpis?.adblue_diesel_ratio !== false && (() => {
                    return (
                        <KPI_RATIO
                            dataSourceRatio={processedDashboardData?.fuelKpis?.adblueDieselRatio || 0}
                            processedDashboardDataSource1={processedDashboardData?.fuelKpis?.adblueThisMonth || 0}
                            processedDashboardDataSource2={processedDashboardData?.fuelKpis?.dieselThisMonth || 0}
                            ratioLabel1="AdBlue"
                            ratioLabel2="Diesel"
                            targetRatioMin={targetAdblueDieselRatioMin}
                            targetRatioMax={targetAdblueDieselRatioMax}
                            title="ADBLUE / DIESEL RATIO"
                            metric='L'
                            icon="⚖️"
                            range="This Month"
                            theme={theme}
                        />
                    );
                })()}

                </div>
            
            <div style={{ marginBottom: '40px' }}>
    
            {/* <AIInsights theme={theme} /> */}
            </div>
            
            {/* Charts Section */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
                gap: '30px',
                marginBottom: '40px'
            }}>
                {/* Daily Jobs Bar Chart */}
                {features?.routing !== false && (
                <div style={{
                    background: theme === 'light' ? '#fff' : '#333',
                    padding: '25px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    border: '1px solid ' + (theme === 'light' ? '#e0e0e0' : '#555')
                }}>
                    <h4 style={{ color: theme === 'light' ? '#333' : '#fff', marginBottom: '20px' }}>
                        📈 Daily Jobs Completed (Last 28 Days)
                    </h4>
                    {processedDashboardData.dailyJobsCompleted ? (
                        <Bar 
                            data={{
                                labels: processedDashboardData.dailyJobsCompleted.map(item => item.date),
                                datasets: [{
                                    label: 'Jobs Completed',
                                    data: processedDashboardData.dailyJobsCompleted.map(item => item.jobs),
                                    backgroundColor: 'rgba(25, 151, 235, 0.6)',
                                    borderColor: 'rgba(25, 151, 235, 1)',
                                    borderWidth: 1
                                }]
                            }}
                            options={{
                                responsive: true,
                                plugins: {
                                    legend: { display: false },
                                    tooltip: {
                                        backgroundColor: theme === 'light' ? '#333' : '#fff',
                                        titleColor: theme === 'light' ? '#fff' : '#333',
                                        bodyColor: theme === 'light' ? '#fff' : '#333'
                                    }
                                },
                                scales: {
                                    y: { 
                                        beginAtZero: true,
                                        ticks: { color: theme === 'light' ? '#666' : '#ccc' },
                                        grid: { color: theme === 'light' ? '#e0e0e0' : '#555' }
                                    },
                                    x: { 
                                        ticks: { color: theme === 'light' ? '#666' : '#ccc' },
                                        grid: { color: theme === 'light' ? '#e0e0e0' : '#555' }
                                    }
                                }
                            }}
                        />
                    ) : (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '40px',
                            color: theme === 'light' ? '#666' : '#ccc'
                        }}>
                            No data available
                        </div>
                    )}
                    
                    {/* Job Statistics Display */}
                    {processedDashboardData.jobStats && (
                        <div style={{ 
                            marginTop: '20px', 
                            padding: '15px', 
                            backgroundColor: theme === 'light' ? '#f8f9fa' : '#2d3748',
                            borderRadius: '8px',
                            border: `1px solid ${theme === 'light' ? '#e2e8f0' : '#4a5568'}`
                        }}>
                            <h5 style={{ 
                                color: theme === 'light' ? '#333' : '#fff', 
                                marginBottom: '15px',
                                textAlign: 'center'
                            }}>
                                🚚 Job Summary
                            </h5>
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-around',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <div style={{ 
                                    textAlign: 'center',
                                    minWidth: '100px'
                                }}>
                                    <div style={{ 
                                        fontSize: '24px', 
                                        fontWeight: 'bold', 
                                        color: '#4CAF50' 
                                    }}>
                                        {processedDashboardData.jobStats.thisWeek}
                                    </div>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        color: theme === 'light' ? '#666' : '#ccc' 
                                    }}>
                                        This Week
                                    </div>
                                </div>
                                <div style={{ 
                                    textAlign: 'center',
                                    minWidth: '100px'
                                }}>
                                    <div style={{ 
                                        fontSize: '24px', 
                                        fontWeight: 'bold', 
                                        color: '#2196F3' 
                                    }}>
                                        {processedDashboardData.jobStats.thisMonth}
                                    </div>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        color: theme === 'light' ? '#666' : '#ccc' 
                                    }}>
                                        This Month
                                    </div>
                                </div>
                                <div style={{ 
                                    textAlign: 'center',
                                    minWidth: '100px'
                                }}>
                                    <div style={{ 
                                        fontSize: '24px', 
                                        fontWeight: 'bold', 
                                        color: '#FF9800' 
                                    }}>
                                        {processedDashboardData.jobStats.subbed}
                                    </div>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        color: theme === 'light' ? '#666' : '#ccc' 
                                    }}>
                                        Subcontracted (Month)
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                )}

                {/* Service Status Pie Chart - Commented out in case needed later
                <div style={{
                    background: theme === 'light' ? '#fff' : '#333',
                    padding: '25px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    border: '1px solid ' + (theme === 'light' ? '#e0e0e0' : '#555')
                }}>
                    <h4 style={{ color: theme === 'light' ? '#333' : '#fff', marginBottom: '20px' }}>
                        🔧 Vehicle Service Status
                    </h4>
                    {processedDashboardData.serviceStats ? (
                        <Doughnut 
                            data={{
                                labels: ['Service Overdue', 'Service Up to Date'],
                                datasets: [{
                                    data: [processedDashboardData.serviceStats.overdue, processedDashboardData.serviceStats.inDate],
                                    backgroundColor: ['#dc3545', '#28a745'],
                                    borderColor: theme === 'light' ? '#fff' : '#333',
                                    borderWidth: 2
                                }]
                            }}
                            options={{
                                responsive: true,
                                cutout: '60%', // This creates the donut hole (60% of the radius)
                                plugins: {
                                    legend: { 
                                        position: 'bottom',
                                        labels: { color: theme === 'light' ? '#333' : '#fff' }
                                    },
                                    tooltip: {
                                        backgroundColor: theme === 'light' ? '#333' : '#fff',
                                        titleColor: theme === 'light' ? '#fff' : '#333',
                                        bodyColor: theme === 'light' ? '#fff' : '#333'
                                    }
                                }
                            }}
                        />
                    ) : (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '40px',
                            color: theme === 'light' ? '#666' : '#ccc'
                        }}>
                            No data available
                        </div>
                    )}
                </div>
                */}



                {/* Warehouse Stock Levels */}
                {features?.warehouse !== false && (
                <div style={{
                    background: theme === 'light' ? '#fff' : '#333',
                    padding: '25px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    border: '1px solid ' + (theme === 'light' ? '#e0e0e0' : '#555')
                }}>
                    <h4 style={{ color: theme === 'light' ? '#333' : '#fff', marginBottom: '20px' }}>
                        📦 Warehouse Stock Levels
                    </h4>
                    {processedDashboardData.warehouseStock ? (
                        <Bar 
                            data={{
                                labels: processedDashboardData.warehouseStock.map(item => item.name),
                                datasets: [
                                    {
                                        label: 'Pallet Count',
                                        data: processedDashboardData.warehouseStock.map(item => item.current),
                                        backgroundColor: 'rgba(224, 77, 253, 0.72)',
                                        borderColor: 'rgba(254, 77, 253, 1)',
                                        borderWidth: 1
                                    },
                                ]
                            }}
                            options={{
                                responsive: true,
                                plugins: {
                                    legend: { 
                                        labels: { color: theme === 'light' ? '#333' : '#fff' }
                                    },
                                    tooltip: {
                                        backgroundColor: theme === 'light' ? '#333' : '#fff',
                                        titleColor: theme === 'light' ? '#fff' : '#333',
                                        bodyColor: theme === 'light' ? '#fff' : '#333',
                                        callbacks: {
                                            afterLabel: function(context) {
                                                // const dataIndex = context.dataIndex;
                                                // const productId = actualDashboardData.warehouseStock[dataIndex]?.product_id || 'Unknown Product';
                                                // return `Product ID: ${productId}`;
                                            }
                                        }
                                    }
                                },
                                scales: {
                                    y: { 
                                        beginAtZero: true,
                                        ticks: { color: theme === 'light' ? '#666' : '#ccc' },
                                        grid: { color: theme === 'light' ? '#e0e0e0' : '#555' }
                                    },
                                    x: { 
                                        ticks: { 
                                            color: theme === 'light' ? '#666' : '#ccc',
                                            callback: function(value) {
                                                return this.getLabelForValue(value);
                                            },
                                            maxRotation: 90,
                                            minRotation: 60
                                        },
                                        grid: { color: theme === 'light' ? '#e0e0e0' : '#555' },
                                        title: {
                                            display: false,
                                            text: 'Warehouse Items',
                                            color: theme === 'light' ? '#333' : '#fff'
                                        }
                                    }
                                }
                            }}
                        />
                    ) : (
                        <p style={{ color: theme === 'light' ? '#333' : '#fff' }}>No data available</p>
                    )}
                    
                    {/* Order Counts Display */}
                    {processedDashboardData.orderCounts && (
                        <div style={{ 
                            marginTop: '20px', 
                            padding: '15px', 
                            backgroundColor: theme === 'light' ? '#f8f9fa' : '#2d3748',
                            borderRadius: '8px',
                            border: `1px solid ${theme === 'light' ? '#e2e8f0' : '#4a5568'}`
                        }}>
                            <h5 style={{ 
                                color: theme === 'light' ? '#333' : '#fff', 
                                marginBottom: '15px',
                                textAlign: 'center'
                            }}>
                                📋 Order Status Summary
                            </h5>
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-around',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <div style={{ 
                                    textAlign: 'center',
                                    minWidth: '100px'
                                }}>
                                    <div style={{ 
                                        fontSize: '24px', 
                                        fontWeight: 'bold', 
                                        color: '#ff6b6b' 
                                    }}>
                                        {processedDashboardData.orderCounts.pending}
                                    </div>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        color: theme === 'light' ? '#666' : '#ccc' 
                                    }}>
                                        Pending
                                    </div>
                                </div>
                                <div style={{ 
                                    textAlign: 'center',
                                    minWidth: '100px'
                                }}>
                                    <div style={{ 
                                        fontSize: '24px', 
                                        fontWeight: 'bold', 
                                        color: '#4ecdc4' 
                                    }}>
                                        {processedDashboardData.orderCounts.picked}
                                    </div>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        color: theme === 'light' ? '#666' : '#ccc' 
                                    }}>
                                        Ready for Dispatch
                                    </div>
                                </div>
                                <div style={{ 
                                    textAlign: 'center',
                                    minWidth: '100px'
                                }}>
                                    <div style={{ 
                                        fontSize: '24px', 
                                        fontWeight: 'bold', 
                                        color: '#45b7d1' 
                                    }}>
                                        {processedDashboardData.orderCounts.thisWeek}
                                    </div>
                                    <div style={{ 
                                        fontSize: '12px', 
                                        color: theme === 'light' ? '#666' : '#ccc' 
                                    }}>
                                        This Week
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                )}
            </div>

            {/* Historical KPI Trends Section */}
            {features?.kpis !== false && <div style={{ 
                marginBottom: '40px',
                background: theme === 'light' ? '#fff' : '#333',
                padding: '25px',
                borderRadius: '12px',
                boxShadow: '0 6px 12px rgba(0,0,0,0.08)',
                border: '1px solid ' + (theme === 'light' ? '#e0e0e0' : '#555')
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                    <h4 style={{ 
                        color: '#6c757d', 
                        fontSize: '18px', 
                        fontWeight: '600', 
                        margin: '0',
                        letterSpacing: '0.5px'
                    }}>
                        📈 HISTORICAL KPI TRENDS
                    </h4>
                    
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {[
                            { value: 1, label: '1M' },
                            { value: 3, label: '3M' },
                            { value: 6, label: '6M' },
                            { value: 12, label: '1Y' },
                            { value: 24, label: '2Y' }
                        ].map((period) => (
                            <button
                                key={period.value}
                                onClick={() => fetchHistoricalKpis(period.value)}
                                disabled={kpiLoading}
                                style={{
                                    padding: '6px 14px',
                                    borderRadius: '6px',
                                    border: '1px solid',
                                    backgroundColor: kpiPeriod === period.value ? '#1997eb' : 
                                                    loadingPeriod === period.value ? '#95a5a6' : 'transparent',
                                    borderColor: kpiPeriod === period.value ? '#1997eb' : 
                                                loadingPeriod === period.value ? '#95a5a6' : '#ccc',
                                    color: (kpiPeriod === period.value || loadingPeriod === period.value) ? '#fff' : 
                                            (theme === 'light' ? '#333' : '#fff'),
                                    cursor: kpiLoading ? 'not-allowed' : 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    opacity: kpiLoading && loadingPeriod !== period.value ? 0.6 : 1,
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {loadingPeriod === period.value ? '...' : period.label}
                            </button>
                        ))}
                    </div>
                </div>
                
                {kpiPeriod === null ? (
                    <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '300px',
                        color: theme === 'light' ? '#666' : '#ccc'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '15px' }}>📊</div>
                        <p style={{ 
                            margin: 0, 
                            fontSize: '18px', 
                            fontWeight: '500',
                            marginBottom: '8px'
                        }}>
                            Historical KPI Analysis
                        </p>
                        <p style={{ 
                            margin: 0, 
                            fontSize: '14px',
                            opacity: 0.8
                        }}>
                            Select a time period above to view KPI trends
                        </p>
                    </div>
                ) : kpiLoading ? (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '300px',
                        color: theme === 'light' ? '#666' : '#ccc'
                    }}>
                        Loading historical data...
                    </div>
                ) : historicalKpis.length > 0 ? (
                    <>
                        <p style={{ 
                            color: theme === 'light' ? '#666' : '#ccc',
                            textAlign: 'center',
                            margin: '0 0 20px 0',
                            fontSize: '14px'
                        }}>
                            Line charts showing KPI trends over the last {kpiPeriod} month{kpiPeriod > 1 ? 's' : ''} • Based on weekly snapshots
                        </p>
                        
                        <div style={{ 
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                            gap: '25px'
                        }}>
                            {/* Operational Risk Trend */}
                            <div style={{
                                background: theme === 'light' ? '#f8f9fa' : '#2d3748',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid ' + (theme === 'light' ? '#e2e8f0' : '#4a5568')
                            }}>
                                <h6 style={{ 
                                    color: theme === 'light' ? '#333' : '#fff',
                                    marginBottom: '15px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    🚨 Operational Risk Trend
                                </h6>
                                <div style={{ height: '200px' }}>
                                    <Line 
                                        data={{
                                            labels: historicalKpis.map(item => item.week_ending),
                                            datasets: [{
                                                label: 'Risk Score',
                                                data: historicalKpis.map(item => item.operational_risk_total || 0),
                                                borderColor: '#dc3545',
                                                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                                                tension: 0.4,
                                                pointRadius: 4,
                                                pointHoverRadius: 6
                                            }, {
                                                label: 'Target',
                                                data: historicalKpis.map(() => targetOperationalRisk),
                                                borderColor: '#95a5a6',
                                                borderWidth: 2,
                                                borderDash: [5, 5],
                                                fill: false,
                                                pointRadius: 0,
                                                tension: 0
                                            }]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                                    titleColor: '#fff',
                                                    bodyColor: '#fff',
                                                    borderColor: '#dc3545',
                                                    borderWidth: 1,
                                                    filter: function(tooltipItem) {
                                                        return tooltipItem.datasetIndex === 0;
                                                    },
                                                    callbacks: {
                                                        label: function(context) {
                                                            return `Risk Score: ${context.parsed.y}`;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc', maxRotation: 45 }
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    grid: { color: theme === 'light' ? '#e2e8f0' : '#4a5568' },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc' }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Profit Margin Trend */}
                            <div style={{
                                background: theme === 'light' ? '#f8f9fa' : '#2d3748',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid ' + (theme === 'light' ? '#e2e8f0' : '#4a5568')
                            }}>
                                <h6 style={{ 
                                    color: theme === 'light' ? '#333' : '#fff',
                                    marginBottom: '15px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    💰 Subcontracted Profit Trend
                                </h6>
                                <div style={{ height: '200px' }}>
                                    <Line 
                                        data={{
                                            labels: historicalKpis.map(item => item.week_ending),
                                            datasets: [{
                                                label: 'Profit %',
                                                data: historicalKpis.map(item => item.subcontracted_profit_percentage || 0),
                                                borderColor: '#28a745',
                                                backgroundColor: 'rgba(40, 167, 69, 0.1)',
                                                tension: 0.4,
                                                pointRadius: 4,
                                                pointHoverRadius: 6
                                            }, {
                                                label: 'Target',
                                                data: historicalKpis.map(() => targetSubcontractedProfit),
                                                borderColor: '#95a5a6',
                                                borderWidth: 2,
                                                borderDash: [5, 5],
                                                fill: false,
                                                pointRadius: 0,
                                                tension: 0
                                            }]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                                    titleColor: '#fff',
                                                    bodyColor: '#fff',
                                                    borderColor: '#28a745',
                                                    borderWidth: 1,
                                                    filter: function(tooltipItem) {
                                                        return tooltipItem.datasetIndex === 0;
                                                    },
                                                    callbacks: {
                                                        label: function(context) {
                                                            return `Profit: ${context.parsed.y}%`;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc', maxRotation: 45 }
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    grid: { color: theme === 'light' ? '#e2e8f0' : '#4a5568' },
                                                    ticks: { 
                                                        color: theme === 'light' ? '#666' : '#ccc',
                                                        callback: function(value) { return value + '%'; }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Weekly Revenue Trend */}
                            <div style={{
                                background: theme === 'light' ? '#f8f9fa' : '#2d3748',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid ' + (theme === 'light' ? '#e2e8f0' : '#4a5568')
                            }}>
                                <h6 style={{ 
                                    color: theme === 'light' ? '#333' : '#fff',
                                    marginBottom: '15px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    📊 Weekly Revenue Trend
                                </h6>
                                <div style={{ height: '200px' }}>
                                    <Line 
                                        data={{
                                            labels: historicalKpis.map(item => item.week_ending),
                                            datasets: [{
                                                label: 'Revenue (£)',
                                                data: historicalKpis.map(item => item.weekly_revenue_this_week || 0),
                                                borderColor: '#1997eb',
                                                backgroundColor: 'rgba(25, 151, 235, 0.1)',
                                                tension: 0.4,
                                                pointRadius: 4,
                                                pointHoverRadius: 6
                                            }, {
                                                label: 'Target',
                                                data: historicalKpis.map(() => targetWeeklyRevenueAmount),
                                                borderColor: '#95a5a6',
                                                borderWidth: 2,
                                                borderDash: [5, 5],
                                                fill: false,
                                                pointRadius: 0,
                                                tension: 0
                                            }]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                                    titleColor: '#fff',
                                                    bodyColor: '#fff',
                                                    borderColor: '#1997eb',
                                                    borderWidth: 1,
                                                    filter: function(tooltipItem) {
                                                        return tooltipItem.datasetIndex === 0;
                                                    },
                                                    callbacks: {
                                                        label: function(context) {
                                                            return `Revenue: £${context.parsed.y.toFixed(2)}`;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc', maxRotation: 45 }
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    grid: { color: theme === 'light' ? '#e2e8f0' : '#4a5568' },
                                                    ticks: { 
                                                        color: theme === 'light' ? '#666' : '#ccc',
                                                        callback: function(value) { return '£' + value; }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Collection Performance Trend */}
                            <div style={{
                                background: theme === 'light' ? '#f8f9fa' : '#2d3748',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid ' + (theme === 'light' ? '#e2e8f0' : '#4a5568')
                            }}>
                                <h6 style={{ 
                                    color: theme === 'light' ? '#333' : '#fff',
                                    marginBottom: '15px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    📦 Daily Collections Average
                                </h6>
                                <div style={{ height: '200px' }}>
                                    <Line 
                                        data={{
                                            labels: historicalKpis.map(item => item.week_ending),
                                            datasets: [{
                                                label: 'Collections',
                                                data: historicalKpis.map(item => item.collections_daily_average || 0),
                                                borderColor: '#ffc107',
                                                backgroundColor: 'rgba(255, 193, 7, 0.1)',
                                                tension: 0.4,
                                                pointRadius: 4,
                                                pointHoverRadius: 6
                                            }, {
                                                label: 'Target',
                                                data: historicalKpis.map(() => targetCollections / 5),
                                                borderColor: '#95a5a6',
                                                borderWidth: 2,
                                                borderDash: [5, 5],
                                                fill: false,
                                                pointRadius: 0,
                                                tension: 0
                                            }]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                                    titleColor: '#fff',
                                                    bodyColor: '#fff',
                                                    borderColor: '#ffc107',
                                                    borderWidth: 1,
                                                    filter: function(tooltipItem) {
                                                        return tooltipItem.datasetIndex === 0;
                                                    },
                                                    callbacks: {
                                                        label: function(context) {
                                                            return `Collections: ${context.parsed.y.toFixed(1)} pallets/day`;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc', maxRotation: 45 }
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    grid: { color: theme === 'light' ? '#e2e8f0' : '#4a5568' },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc' }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Outstanding Defects Trend */}
                            <div style={{
                                background: theme === 'light' ? '#f8f9fa' : '#2d3748',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid ' + (theme === 'light' ? '#e2e8f0' : '#4a5568')
                            }}>
                                <h6 style={{ 
                                    color: theme === 'light' ? '#333' : '#fff',
                                    marginBottom: '15px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    🔧 Outstanding Defects Trend
                                </h6>
                                <div style={{ height: '200px' }}>
                                    <Line 
                                        data={{
                                            labels: historicalKpis.map(item => item.week_ending),
                                            datasets: [{
                                                label: 'Outstanding Defects',
                                                data: historicalKpis.map(item => item.outstanding_defects || 0),
                                                borderColor: '#e74c3c',
                                                backgroundColor: 'rgba(231, 76, 60, 0.1)',
                                                tension: 0.4,
                                                pointRadius: 4,
                                                pointHoverRadius: 6
                                            }, {
                                                label: 'Target',
                                                data: historicalKpis.map(() => targetOutstandingDefects),
                                                borderColor: '#95a5a6',
                                                borderWidth: 2,
                                                borderDash: [5, 5],
                                                fill: false,
                                                pointRadius: 0,
                                                tension: 0
                                            }]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                                    titleColor: '#fff',
                                                    bodyColor: '#fff',
                                                    borderColor: '#e74c3c',
                                                    borderWidth: 1,
                                                    filter: function(tooltipItem) {
                                                        return tooltipItem.datasetIndex === 0;
                                                    },
                                                    callbacks: {
                                                        label: function(context) {
                                                            return `Outstanding: ${context.parsed.y} defects`;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc', maxRotation: 45 }
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    grid: { color: theme === 'light' ? '#e2e8f0' : '#4a5568' },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc' }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Unsigned Tacho Trend */}
                            <div style={{
                                background: theme === 'light' ? '#f8f9fa' : '#2d3748',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid ' + (theme === 'light' ? '#e2e8f0' : '#4a5568')
                            }}>
                                <h6 style={{ 
                                    color: theme === 'light' ? '#333' : '#fff',
                                    marginBottom: '15px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    📋 Unsigned Tacho Trend
                                </h6>
                                <div style={{ height: '200px' }}>
                                    <Line 
                                        data={{
                                            labels: historicalKpis.map(item => item.week_ending),
                                            datasets: [{
                                                label: 'Unsigned Tacho',
                                                data: historicalKpis.map(item => item.unsigned_tacho || 0),
                                                borderColor: '#f39c12',
                                                backgroundColor: 'rgba(243, 156, 18, 0.1)',
                                                tension: 0.4,
                                                pointRadius: 4,
                                                pointHoverRadius: 6
                                            }, {
                                                label: 'Target',
                                                data: historicalKpis.map(() => targetTachoCompliance),
                                                borderColor: '#95a5a6',
                                                borderWidth: 2,
                                                borderDash: [5, 5],
                                                fill: false,
                                                pointRadius: 0,
                                                tension: 0
                                            }]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                                    titleColor: '#fff',
                                                    bodyColor: '#fff',
                                                    borderColor: '#f39c12',
                                                    borderWidth: 1,
                                                    filter: function(tooltipItem) {
                                                        return tooltipItem.datasetIndex === 0;
                                                    },
                                                    callbacks: {
                                                        label: function(context) {
                                                            return `Unsigned: ${context.parsed.y} tacho entries`;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc', maxRotation: 45 }
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    grid: { color: theme === 'light' ? '#e2e8f0' : '#4a5568' },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc' }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Defect Completion Rate Trend */}
                            <div style={{
                                background: theme === 'light' ? '#f8f9fa' : '#2d3748',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid ' + (theme === 'light' ? '#e2e8f0' : '#4a5568')
                            }}>
                                <h6 style={{ 
                                    color: theme === 'light' ? '#333' : '#fff',
                                    marginBottom: '15px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    ✅ Defect Completion Rate
                                </h6>
                                <div style={{ height: '200px' }}>
                                    <Line 
                                        data={{
                                            labels: historicalKpis.map(item => item.week_ending),
                                            datasets: [{
                                                label: 'Completion Rate',
                                                data: historicalKpis.map(item => item.defect_completion_rate || 0),
                                                borderColor: '#27ae60',
                                                backgroundColor: 'rgba(39, 174, 96, 0.1)',
                                                tension: 0.4,
                                                pointRadius: 4,
                                                pointHoverRadius: 6
                                            }, {
                                                label: 'Target',
                                                data: historicalKpis.map(() => targetDefectResolution),
                                                borderColor: '#95a5a6',
                                                borderWidth: 2,
                                                borderDash: [5, 5],
                                                fill: false,
                                                pointRadius: 0,
                                                tension: 0
                                            }]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                                    titleColor: '#fff',
                                                    bodyColor: '#fff',
                                                    borderColor: '#27ae60',
                                                    borderWidth: 1,
                                                    filter: function(tooltipItem) {
                                                        return tooltipItem.datasetIndex === 0;
                                                    },
                                                    callbacks: {
                                                        label: function(context) {
                                                            return `Completion Rate: ${context.parsed.y}%`;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc', maxRotation: 45 }
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    max: 100,
                                                    grid: { color: theme === 'light' ? '#e2e8f0' : '#4a5568' },
                                                    ticks: { 
                                                        color: theme === 'light' ? '#666' : '#ccc',
                                                        callback: function(value) { return value + '%'; }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Service Compliance Trend */}
                            <div style={{
                                background: theme === 'light' ? '#f8f9fa' : '#2d3748',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid ' + (theme === 'light' ? '#e2e8f0' : '#4a5568')
                            }}>
                                <h6 style={{ 
                                    color: theme === 'light' ? '#333' : '#fff',
                                    marginBottom: '15px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    🔧 Service Compliance Rate
                                </h6>
                                <div style={{ height: '200px' }}>
                                    <Line 
                                        data={{
                                            labels: historicalKpis.map(item => item.week_ending),
                                            datasets: [{
                                                label: 'Service Compliance',
                                                data: historicalKpis.map(item => item.service_compliance_percentage || 0),
                                                borderColor: '#3498db',
                                                backgroundColor: 'rgba(52, 152, 219, 0.1)',
                                                tension: 0.4,
                                                pointRadius: 4,
                                                pointHoverRadius: 6
                                            }, {
                                                label: 'Target',
                                                data: historicalKpis.map(() => targetServiceCompliance),
                                                borderColor: '#95a5a6',
                                                borderWidth: 2,
                                                borderDash: [5, 5],
                                                fill: false,
                                                pointRadius: 0,
                                                tension: 0
                                            }]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                                    titleColor: '#fff',
                                                    bodyColor: '#fff',
                                                    borderColor: '#3498db',
                                                    borderWidth: 1,
                                                    filter: function(tooltipItem) {
                                                        return tooltipItem.datasetIndex === 0;
                                                    },
                                                    callbacks: {
                                                        label: function(context) {
                                                            return `Service Compliance: ${context.parsed.y}%`;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc', maxRotation: 45 }
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    max: 100,
                                                    grid: { color: theme === 'light' ? '#e2e8f0' : '#4a5568' },
                                                    ticks: { 
                                                        color: theme === 'light' ? '#666' : '#ccc',
                                                        callback: function(value) { return value + '%'; }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Driver Testing Compliance Trend */}
                            <div style={{
                                background: theme === 'light' ? '#f8f9fa' : '#2d3748',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid ' + (theme === 'light' ? '#e2e8f0' : '#4a5568')
                            }}>
                                <h6 style={{ 
                                    color: theme === 'light' ? '#333' : '#fff',
                                    marginBottom: '15px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    🧪 Driver Testing Compliance
                                </h6>
                                <div style={{ height: '200px' }}>
                                    <Line 
                                        data={{
                                            labels: historicalKpis.map(item => item.week_ending),
                                            datasets: [{
                                                label: 'Testing Compliance',
                                                data: historicalKpis.map(item => item.driver_testing_percentage || 0),
                                                borderColor: '#9b59b6',
                                                backgroundColor: 'rgba(155, 89, 182, 0.1)',
                                                tension: 0.4,
                                                pointRadius: 4,
                                                pointHoverRadius: 6
                                            }, {
                                                label: 'Target',
                                                data: historicalKpis.map(() => targetDriverTesting),
                                                borderColor: '#95a5a6',
                                                borderWidth: 2,
                                                borderDash: [5, 5],
                                                fill: false,
                                                pointRadius: 0,
                                                tension: 0
                                            }]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                                    titleColor: '#fff',
                                                    bodyColor: '#fff',
                                                    borderColor: '#9b59b6',
                                                    borderWidth: 1,
                                                    filter: function(tooltipItem) {
                                                        return tooltipItem.datasetIndex === 0;
                                                    },
                                                    callbacks: {
                                                        label: function(context) {
                                                            return `Testing Compliance: ${context.parsed.y}%`;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc', maxRotation: 45 }
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    max: 100,
                                                    grid: { color: theme === 'light' ? '#e2e8f0' : '#4a5568' },
                                                    ticks: { 
                                                        color: theme === 'light' ? '#666' : '#ccc',
                                                        callback: function(value) { return value + '%'; }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            {/* Diesel L/km Trend */}
                            <div style={{
                                background: theme === 'light' ? '#f8f9fa' : '#2d3748',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid ' + (theme === 'light' ? '#e2e8f0' : '#4a5568')
                            }}>
                                <h6 style={{ 
                                    color: theme === 'light' ? '#333' : '#fff',
                                    marginBottom: '15px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    ⛽ Diesel Efficiency (L/km)
                                </h6>
                                <div style={{ height: '200px' }}>
                                    <Line 
                                        data={{
                                            labels: historicalKpis.map(item => item.week_ending),
                                            datasets: [{
                                                label: 'L/km',
                                                data: historicalKpis.map(item => item.litres_per_km || 0),
                                                borderColor: '#e67e22',
                                                backgroundColor: 'rgba(230, 126, 34, 0.1)',
                                                tension: 0.4,
                                                pointRadius: 4,
                                                pointHoverRadius: 6
                                            }, {
                                                label: 'Target',
                                                data: historicalKpis.map(() => targetDieselUsage),
                                                borderColor: '#95a5a6',
                                                borderWidth: 2,
                                                borderDash: [5, 5],
                                                fill: false,
                                                pointRadius: 0,
                                                tension: 0
                                            }]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                                    titleColor: '#fff',
                                                    bodyColor: '#fff',
                                                    borderColor: '#e67e22',
                                                    borderWidth: 1,
                                                    filter: function(tooltipItem) {
                                                        return tooltipItem.datasetIndex === 0;
                                                    },
                                                    callbacks: {
                                                        label: function(context) {
                                                            return `L/km: ${context.parsed.y.toFixed(2)}`;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc', maxRotation: 45 }
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    grid: { color: theme === 'light' ? '#e2e8f0' : '#4a5568' },
                                                    ticks: {
                                                        color: theme === 'light' ? '#666' : '#ccc',
                                                        callback: function(value) { return value.toFixed(2); }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* AdBlue/Diesel Ratio Trend */}
                            <div style={{
                                background: theme === 'light' ? '#f8f9fa' : '#2d3748',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid ' + (theme === 'light' ? '#e2e8f0' : '#4a5568')
                            }}>
                                <h6 style={{ 
                                    color: theme === 'light' ? '#333' : '#fff',
                                    marginBottom: '15px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    💧 AdBlue / Diesel Ratio
                                </h6>
                                <div style={{ height: '200px' }}>
                                    <Line 
                                        data={{
                                            labels: historicalKpis.map(item => item.week_ending),
                                            datasets: [{
                                                label: 'AdBlue/Diesel Ratio',
                                                data: historicalKpis.map(item => item.adblue_diesel_ratio || 0),
                                                borderColor: '#3498db',
                                                backgroundColor: 'rgba(52, 152, 219, 0.1)',
                                                tension: 0.4,
                                                pointRadius: 4,
                                                pointHoverRadius: 6
                                            }, {
                                                label: `Min (${targetAdblueDieselRatioMin.toFixed(1)}%)`,
                                                data: historicalKpis.map(() => targetAdblueDieselRatioMin),
                                                borderColor: '#27ae60',
                                                borderWidth: 1,
                                                borderDash: [5, 5],
                                                fill: false,
                                                pointRadius: 0,
                                                tension: 0
                                            }, {
                                                label: `Max (${targetAdblueDieselRatioMax.toFixed(1)}%)`,
                                                data: historicalKpis.map(() => targetAdblueDieselRatioMax),
                                                borderColor: '#e74c3c',
                                                borderWidth: 1,
                                                borderDash: [5, 5],
                                                fill: false,
                                                pointRadius: 0,
                                                tension: 0
                                            }]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                                    titleColor: '#fff',
                                                    bodyColor: '#fff',
                                                    borderColor: '#3498db',
                                                    borderWidth: 1,
                                                    filter: function(tooltipItem) {
                                                        return tooltipItem.datasetIndex === 0;
                                                    },
                                                    callbacks: {
                                                        label: function(context) {
                                                            return `AdBlue/Diesel: ${context.parsed.y.toFixed(1)}%`;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc', maxRotation: 45 }
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    grid: { color: theme === 'light' ? '#e2e8f0' : '#4a5568' },
                                                    ticks: {
                                                        color: theme === 'light' ? '#666' : '#ccc',
                                                        callback: function(value) { return value + '%'; }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Diesel Usage Trend */}
                            <div style={{
                                background: theme === 'light' ? '#f8f9fa' : '#2d3748',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid ' + (theme === 'light' ? '#e2e8f0' : '#4a5568')
                            }}>
                                <h6 style={{ 
                                    color: theme === 'light' ? '#333' : '#fff',
                                    marginBottom: '15px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    ⛽ Diesel Usage (Litres)
                                </h6>
                                <div style={{ height: '200px' }}>
                                    <Line 
                                        data={{
                                            labels: historicalKpis.map(item => item.week_ending),
                                            datasets: [{
                                                label: 'Diesel (L)',
                                                data: historicalKpis.map(item => item.diesel_litres || 0),
                                                borderColor: '#e67e22',
                                                backgroundColor: 'rgba(230, 126, 34, 0.1)',
                                                tension: 0.4,
                                                pointRadius: 4,
                                                pointHoverRadius: 6
                                            }]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                                    titleColor: '#fff',
                                                    bodyColor: '#fff',
                                                    borderColor: '#e67e22',
                                                    borderWidth: 1,
                                                    callbacks: {
                                                        label: function(context) {
                                                            return `Diesel: ${context.parsed.y.toLocaleString()}L`;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc', maxRotation: 45 }
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    grid: { color: theme === 'light' ? '#e2e8f0' : '#4a5568' },
                                                    ticks: {
                                                        color: theme === 'light' ? '#666' : '#ccc',
                                                        callback: function(value) { return value.toLocaleString() + 'L'; }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* AdBlue Usage Trend */}
                            <div style={{
                                background: theme === 'light' ? '#f8f9fa' : '#2d3748',
                                padding: '20px',
                                borderRadius: '8px',
                                border: '1px solid ' + (theme === 'light' ? '#e2e8f0' : '#4a5568')
                            }}>
                                <h6 style={{ 
                                    color: theme === 'light' ? '#333' : '#fff',
                                    marginBottom: '15px',
                                    fontSize: '14px',
                                    fontWeight: '600'
                                }}>
                                    💧 AdBlue Usage (Litres)
                                </h6>
                                <div style={{ height: '200px' }}>
                                    <Line 
                                        data={{
                                            labels: historicalKpis.map(item => item.week_ending),
                                            datasets: [{
                                                label: 'AdBlue (L)',
                                                data: historicalKpis.map(item => item.adblue_litres || 0),
                                                borderColor: '#3498db',
                                                backgroundColor: 'rgba(52, 152, 219, 0.1)',
                                                tension: 0.4,
                                                pointRadius: 4,
                                                pointHoverRadius: 6
                                            }]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    backgroundColor: 'rgba(0,0,0,0.8)',
                                                    titleColor: '#fff',
                                                    bodyColor: '#fff',
                                                    borderColor: '#3498db',
                                                    borderWidth: 1,
                                                    callbacks: {
                                                        label: function(context) {
                                                            return `AdBlue: ${context.parsed.y.toLocaleString()}L`;
                                                        }
                                                    }
                                                }
                                            },
                                            scales: {
                                                x: {
                                                    grid: { display: false },
                                                    ticks: { color: theme === 'light' ? '#666' : '#ccc', maxRotation: 45 }
                                                },
                                                y: {
                                                    beginAtZero: true,
                                                    grid: { color: theme === 'light' ? '#e2e8f0' : '#4a5568' },
                                                    ticks: {
                                                        color: theme === 'light' ? '#666' : '#ccc',
                                                        callback: function(value) { return value.toLocaleString() + 'L'; }
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '40px',
                        color: theme === 'light' ? '#666' : '#ccc'
                    }}>
                        <p style={{ margin: 0 }}>No historical data available for the selected period</p>
                        <p style={{ margin: '10px 0 0 0', fontSize: '14px' }}>
                            Historical data is generated from weekly KPI snapshots
                        </p>
                    </div>
                )}
            </div>}
        </div>
    )
}
